import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '../UI-CHAKRA/Box';
import { Text } from '../UI-CHAKRA/Text';
import {
  DEFAULT_PRIMARY_COLOR,
  DEFAULT_SECONDARY_COLOR,
} from '../../containers/prescription/usePrescription/helpers.mock';
import { Button } from '../UI-CHAKRA/Button';
import { Flex } from '../UI-CHAKRA/Flex';
import { ANCHORS, ANCHOR_POS } from './notifications';
import '../../containers/prescription/animations.css';
import { AnimatePresence } from 'framer-motion';
import { FadeMotionBox } from '../Motion/FadeMotionBox';
import { usePrescriptionStore } from '../../stores/prescriptionStore';

interface NotificationTooltipProps {
  parentAnchorName: ANCHORS;
  position: ANCHOR_POS;
  negativeGap: number;
  onReadNotification(): void;
}

const SAFE_GAP = 15;

export const NotificationTooltip = (props: NotificationTooltipProps) => {
  const { prescriberConfigurations } = usePrescriptionStore();

  const [rootElement, setRootElement] = useState<HTMLElement | null>(null);
  const [parentElement, setParentElement] = useState<HTMLElement | null>(null);
  const [tooltipElement, setTooltipElement] = useState<HTMLElement | null>(null);

  const [rootElementPos, setRootElementPos] = useState({ height: 0, width: 0 });
  const [tooltipElementPos, setTooltipElementPos] = useState({ height: 0, width: 0 });
  const [parentElementPos, setParentElementPos] = useState({ width: 0 });

  useEffect(() => {
    const root = document.getElementById(`root-anchor-${props.parentAnchorName}`);
    const parent = document.getElementById(props.parentAnchorName);

    setRootElement(root);
    setParentElement(parent);

    const timeoutId = setTimeout(() => {
      const tooltip = document.getElementById(`notification-tooltip-${props.parentAnchorName}`);
      if (tooltip) {
        setTooltipElement(tooltip);
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [props.parentAnchorName]);

  useEffect(() => {
    if (!parentElement) return;

    let resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) {
            return;
          }

          setParentElementPos({ width: entry.contentRect.width });
        });
      });
    });

    resizeObserver.observe(parentElement);

    return () => resizeObserver.disconnect();
  }, [parentElement]);

  useEffect(() => {
    if (!tooltipElement) {
      let attempts = 0;
      const maxAttempts = 10;

      const tryFindTooltip = () => {
        const tooltip = document.getElementById(`notification-tooltip-${props.parentAnchorName}`);
        if (tooltip) {
          setTooltipElement(tooltip);
        } else if (attempts < maxAttempts) {
          attempts++;
          requestAnimationFrame(tryFindTooltip);
        }
      };

      const frameId = requestAnimationFrame(tryFindTooltip);
      return () => cancelAnimationFrame(frameId);
    }

    let resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) {
            return;
          }

          setTooltipElementPos({
            height: entry.contentRect.height,
            width: entry.contentRect.width
          });
        });
      });
    });

    resizeObserver.observe(tooltipElement);

    return () => resizeObserver.disconnect();
  }, [tooltipElement, props.parentAnchorName]);

  useEffect(() => {
    if (!rootElement) return;

    let resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) {
            return;
          }

          setRootElementPos({
            height: entry.contentRect.height,
            width: entry.contentRect.width
          });
        });
      });
    });

    resizeObserver.observe(rootElement);

    return () => resizeObserver.disconnect();
  }, [rootElement]);

  const calculateTooltipPosition = (
    position: ANCHOR_POS,
    parentWidth: number,
    tooltipHeight: number,
    tooltipWidth: number,
    rootHeight: number,
    rootWidth: number,
    negativeGap: number
  ) => {
    const positionMap = {
      [ANCHOR_POS.RIGHT]: { left: parentWidth + SAFE_GAP - negativeGap },
      [ANCHOR_POS.LEFT]: { right: parentWidth + SAFE_GAP - negativeGap },
      [ANCHOR_POS.TOP]: { top: -(tooltipHeight + SAFE_GAP * 4) },
      [ANCHOR_POS.TOP_MOBILE]: { top: -(tooltipHeight + SAFE_GAP * 4), width: 100 },
      [ANCHOR_POS.BOTTOM]: { top: rootHeight + SAFE_GAP },
      [ANCHOR_POS.TOP_LEFT]: { top: -(tooltipHeight + SAFE_GAP * 4), right: parentWidth + SAFE_GAP - negativeGap },
      [ANCHOR_POS.TOP_RIGHT]: {
        top: -(tooltipHeight + SAFE_GAP * 4),
        left: '50%',
        transform: 'translateX(-50%)'
      },
      [ANCHOR_POS.BOTTOM_RIGHT]: {
        top: rootHeight + SAFE_GAP,
        left: '50%',
        transform: 'translateX(-50%)'
      },
    };
    return positionMap[position] || {};
  };

  const tooltipPosition = useMemo(() => {
    return calculateTooltipPosition(
      props.position,
      parentElementPos.width,
      tooltipElementPos.height,
      tooltipElementPos.width,
      rootElementPos.height,
      rootElementPos.width,
      props.negativeGap
    );
  }, [parentElementPos.width, props.position, props.negativeGap, tooltipElementPos.height, tooltipElementPos.width, rootElementPos.height, rootElementPos.width]);

  const notifications = useMemo(() => {
    return prescriberConfigurations?.Configuracoes?.featureNotifications;
  }, [prescriberConfigurations]);

  const currentNotification = useMemo(() => {
    if (!notifications) return null;

    return notifications[props.parentAnchorName];
  }, [props.parentAnchorName, notifications]);

  useEffect(() => {
    if (!currentNotification) return;

    let attempts = 0;
    const maxAttempts = 30;

    const tryFindTooltip = () => {
      const tooltip = document.getElementById(`notification-tooltip-${props.parentAnchorName}`);
      if (tooltip) {
        setTooltipElement(tooltip);
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(tryFindTooltip, 50);
      }
    };

    const timeoutId = setTimeout(tryFindTooltip, 100);

    return () => clearTimeout(timeoutId);
  }, [currentNotification, props.parentAnchorName]);

  if (!prescriberConfigurations) {
    return null;
  }

  if (!currentNotification) return null;

  return (
    <AnimatePresence>
      <FadeMotionBox>
        <Box
          position={'absolute'}
          top={0}
          zIndex={99999}
          bg={'#FFB6BF'}
          p={'20px 15px'}
          borderRadius={'20px'}
          display={'flex'}
          flexDirection={'column'}
          gap={'10px'}
          id={`notification-tooltip-${props.parentAnchorName}`}
          data-testid={`notification-tooltip-${props.parentAnchorName}`}
          className="secondary-pulse-animation"
          minW={'240px'}
          pointerEvents={'auto'}
          overflow={'visible'}
          style={{
            ...tooltipPosition,
          }}
        >
          <Text
            fontSize={'18px'}
            fontWeight={700}
            color={DEFAULT_PRIMARY_COLOR}
            textAlign={'center'}
            lineHeight={'16px'}
          >
            {currentNotification.title}
          </Text>

          <Text
            fontSize={'16px'}
            fontWeight={400}
            color={DEFAULT_PRIMARY_COLOR}
            lineHeight={'16px'}
            mt={'5px'}
            whiteSpace={'pre-wrap'}
            dangerouslySetInnerHTML={{ __html: currentNotification.body }}
          ></Text>

          <Flex justifyContent={'center'} mt={'10px'}>
            <Button
              borderRadius={'20px'}
              backgroundColor={DEFAULT_PRIMARY_COLOR}
              p={'10px 20px'}
              onClick={props.onReadNotification}
            >
              <Text fontSize={'12px'} fontWeight={700} color={DEFAULT_SECONDARY_COLOR} textAlign={'center'}>
                {currentNotification.buttonLabel}
              </Text>
            </Button>
          </Flex>
        </Box>
      </FadeMotionBox>
    </AnimatePresence>
  );
};
